/**
 * "Sign in as" (design "Sign in as: no tokens to copy"): the three ways Flow signs a call in — an app user (the
 * app's own accounts, vfs_scaffold_auth: pick one; "+ Test user" makes one through the app's sign-up), a visitor of
 * the preview (no app sign-in), a token or header the person gives — and, beside them, Compare roles: the same call as
 * every app user and as a visitor, side by side.
 */
import React, { useState } from 'react';
import PropTypes from 'prop-types';
import { Plus, UsersThree } from '@phosphor-icons/react';
import { DataDialog, Loading } from '../data/DataCommon';
import { StatusPill } from './ApiRail';

const initials = (user) => String(user.name || user.email || '?').split(/[\s@.]+/).filter(Boolean).slice(0, 2).map((w) => w[0].toUpperCase()).join('');

function TestUserForm({ roles, defaultRole, onCreate, onCancel }) {
  const [role, setRole] = useState(defaultRole || roles[0] || '');
  const [name, setName] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState(null);
  const submit = async(event) => {
    event.preventDefault();
    setBusy(true);
    setError(null);
    try {
      await onCreate({ role, name });
    } catch (failure) {
      setError(failure?.body?.error || failure?.message || 'Could not make the test user');
      setBusy(false);
    }
  };
  return (
    <form className="api-testuser" data-testid="api-testuser-form" onSubmit={submit}>
      <label className="data-sr" htmlFor="api-testuser-name">Name</label>
      <input className="data-input" id="api-testuser-name" onChange={(event) => setName(event.target.value)} placeholder="Name (optional)" value={name} />
      <label className="data-sr" htmlFor="api-testuser-role">Role</label>
      <select className="data-input" id="api-testuser-role" onChange={(event) => setRole(event.target.value)} value={role}>
        {roles.map((r) => <option key={r} value={r}>{r}</option>)}
      </select>
      <button className="data-btn is-primary" disabled={busy} type="submit">{busy ? 'Making…' : 'Make test user'}</button>
      <button className="data-btn is-ghost" onClick={onCancel} type="button">Cancel</button>
      {error && <p className="data-error-text data-full" role="alert">{error}</p>}
    </form>
  );
}

TestUserForm.propTypes = { defaultRole: PropTypes.string, onCancel: PropTypes.func.isRequired, onCreate: PropTypes.func.isRequired, roles: PropTypes.arrayOf(PropTypes.string).isRequired };

export default function SignInDialog({ asValue, onPick, onClose, appUsers, canWrite, onCreateTestUser, compare, onCompare, request }) {
  const [making, setMaking] = useState(false);
  const { auth, users = [], loading, error, service, note } = appUsers;
  const kind = asValue.startsWith('user:') ? 'user' : asValue;
  const option = (value, title, text, children = null) => (
    <label className={`api-opt${kind === value ? ' is-on' : ''}`}>
      <input checked={kind === value} name="api-as" onChange={() => onPick(value === 'user' ? (users[0] ? `user:${users[0].id}` : 'user') : value)} type="radio" />
      <span className="api-opt-body">
        <strong>{title}</strong>
        <span className="data-muted">{text}</span>
        {children}
      </span>
    </label>
  );
  return (
    <DataDialog onClose={onClose} subtitle="Flow signs the request in for you. Nothing to copy, nothing that expires in the middle of a test." testId="api-signin-dialog" title="Sign in as" wide>
      <div className="api-signin-grid">
        <div className="api-opts">
          {option('user', 'An app user', auth
            ? `The app has its own accounts (vfs_scaffold_auth${service ? `, ${service.replace(/^services\//, '')}` : ''}). Pick one; Flow logs in through the app's ${auth.prefix}/login and keeps the session for this tab's calls.`
            : 'This project has no app accounts yet: ask the AI to add sign-in (vfs_scaffold_auth) to a service.', auth && (
            <span className="api-users">
              {loading && <Loading label="Loading the app's users…" />}
              {error && <span className="data-error-text">{error}</span>}
              {!loading && !users.length && <span className="data-muted">No users yet: make a test user.</span>}
              {users.map((user) => (
                <button
                  aria-pressed={asValue === `user:${user.id}`}
                  className={`api-user${asValue === `user:${user.id}` ? ' is-on' : ''}`}
                  data-testid="api-user"
                  disabled={user.disabled}
                  key={user.id}
                  onClick={(event) => { event.preventDefault(); onPick(`user:${user.id}`); }}
                  type="button"
                >
                  <span aria-hidden className="api-av">{initials(user)}</span>
                  <span className="api-user-name">{user.name || user.email} · {user.email}</span>
                  {user.roles.map((role) => <span className="data-pill" key={role}>{role}</span>)}
                  {user.testUser && <span className="data-pill">test</span>}
                  {user.signedIn && <span className="data-pill is-ok">signed in</span>}
                </button>
              ))}
              {note && <span className="data-muted">{note}</span>}
              {making
                ? <TestUserForm defaultRole={auth.defaultRole} onCancel={() => setMaking(false)} onCreate={async(input) => { await onCreateTestUser(input); setMaking(false); }} roles={auth.roles} />
                : (
                  <span className="data-row-gap is-wrap">
                    <button className="data-btn" disabled={!canWrite} onClick={(event) => { event.preventDefault(); setMaking(true); }} title={canWrite ? undefined : 'Editors can make test users'} type="button"><Plus aria-hidden size={14} />Test user</button>
                    <button className="data-btn" disabled={!users.length || compare.running} onClick={(event) => { event.preventDefault(); onCompare(); }} type="button"><UsersThree aria-hidden size={14} />Compare roles</button>
                  </span>
                )}
            </span>
          ))}
          {option('visitor', 'A visitor of the preview (no app sign-in)', 'As someone who opened the preview: passes Flow\'s own check, no app account. To see what an anonymous user gets.')}
          {option('token', 'A token or header you give', 'For an API secured another way: an Authorization header or an API key. Kept in this browser only, masked in History and in "Copy as curl".')}
        </div>
        <aside aria-labelledby="api-cmp-title" className="api-card api-compare" data-testid="api-compare">
          <h3 id="api-cmp-title">Compare roles: {request.method} {request.path}</h3>
          <p className="data-muted">The same call as every app user, side by side: a quick check of the app&apos;s permissions.</p>
          {compare.rows.length > 0 && (
            <div className="api-compare-grid">
              {compare.rows.map((row) => (
                <React.Fragment key={row.key}>
                  <strong>{row.label}</strong>
                  {row.status === undefined ? <span className="data-muted">…</span> : <StatusPill error={row.error} status={row.status} />}
                  <span className="data-mono api-compare-result">{row.result}</span>
                </React.Fragment>
              ))}
            </div>
          )}
          <button className="data-btn is-primary" disabled={!users.length || compare.running} onClick={onCompare} type="button">{compare.running ? 'Running…' : 'Run for every user'}</button>
          {compare.warning && <p className="data-footnote">{compare.warning}</p>}
        </aside>
      </div>
    </DataDialog>
  );
}

SignInDialog.propTypes = {
  appUsers: PropTypes.shape({ auth: PropTypes.object, users: PropTypes.array, loading: PropTypes.bool, error: PropTypes.string, service: PropTypes.string, note: PropTypes.string }).isRequired,
  asValue: PropTypes.string.isRequired,
  canWrite: PropTypes.bool,
  compare: PropTypes.shape({ rows: PropTypes.array, running: PropTypes.bool, warning: PropTypes.string }).isRequired,
  onClose: PropTypes.func.isRequired,
  onCompare: PropTypes.func.isRequired,
  onCreateTestUser: PropTypes.func.isRequired,
  onPick: PropTypes.func.isRequired,
  request: PropTypes.object.isRequired,
};
